/**
 * Growth Media — interaction layer.
 *
 * Vanilla ES module, no jQuery. Null-safe enhancements that each run only where
 * their markup exists:
 *
 *   1. Reveal-on-scroll — IntersectionObserver adds .is-visible to .gms-reveal.
 *   2. Card carousels  — sync pagination dots with the scroll-snap track
 *      (mobile only; desktop is a static grid). data-gms-carousel.
 *
 * Per Figma the "es para ti si…" cards are flat/static (no accordion), so the
 * old accordion enhancement was removed. The CSS owns the reveal transition
 * (opacity/transform); this module only flips classes. It degrades cleanly:
 * missing elements → no-op, missing IntersectionObserver → content stays visible.
 */

/* -------------------------------------------------------------------------- */
/* 1. Reveal-on-scroll                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Reveal .gms-reveal elements as they scroll into view. The hidden state in
 * base-gms.css is gated behind .has-js-reveal on <html>, so this gate is only
 * added when IntersectionObserver is available — otherwise content stays fully
 * visible (fails open).
 */
function initReveal() {
  const targets = document.querySelectorAll('.gms-reveal');
  if (!targets.length) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    return;
  }

  document.documentElement.classList.add('has-js-reveal');

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  targets.forEach((target) => observer.observe(target));
}

/* -------------------------------------------------------------------------- */
/* 2. Card carousels                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Wire each [data-gms-carousel]: clicking a dot scrolls its card into view, and
 * scrolling the track lights the dot of the card closest to the viewport
 * centre. rAF-throttled, passive scroll listener. On desktop the viewport is a
 * static grid (no overflow) and the dots are hidden, so the handlers are inert.
 */
function initCarousels() {
  const carousels = document.querySelectorAll('[data-gms-carousel]');
  if (!carousels.length) {
    return;
  }

  carousels.forEach((root) => {
    const viewport = root.querySelector('.gms-carousel__viewport');
    const slides = Array.from(root.querySelectorAll('.gms-carousel__slide'));
    const dots = Array.from(root.querySelectorAll('.gms-carousel__dot'));
    if (!viewport || slides.length === 0 || dots.length === 0) {
      return;
    }

    const setActive = (index) => {
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slide = slides[i];
        if (slide) {
          viewport.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
      });
    });

    let ticking = false;
    viewport.addEventListener(
      'scroll',
      () => {
        if (ticking) {
          return;
        }
        ticking = true;
        window.requestAnimationFrame(() => {
          const centre = viewport.scrollLeft + viewport.clientWidth / 2;
          let active = 0;
          let nearest = Infinity;
          slides.forEach((slide, i) => {
            const slideCentre = slide.offsetLeft + slide.clientWidth / 2;
            const distance = Math.abs(slideCentre - centre);
            if (distance < nearest) {
              nearest = distance;
              active = i;
            }
          });
          setActive(active);
          ticking = false;
        });
      },
      { passive: true }
    );
  });
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                  */
/* -------------------------------------------------------------------------- */

function init() {
  initReveal();
  initCarousels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
